import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export type UserType = "artist" | "fan" | null;

interface SignupData {
  // Common fields
  email: string;
  password: string;
  userType: UserType;
  
  // Artist-specific fields
  artistName: string;
  artistCity: string;
  artistCountry: string;
  artistGenres: string[];
  artistTagline: string;
  artistProfileImage: string | null;
  artistInstagram: string;
  artistTiktok: string;
  artistWebsite: string;
  artistSpotify: string;
  
  // Fan-specific fields
  fanDisplayName: string;
  fanCity: string;
  fanCountry: string;
  fanGenres: string[];
  fanProfileImage: string | null;
}

interface SignupWizardContextType {
  data: SignupData;
  updateData: (updates: Partial<SignupData>) => void;
  resetData: () => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
}

const initialData: SignupData = {
  email: "",
  password: "",
  userType: null,
  artistName: "",
  artistCity: "",
  artistCountry: "",
  artistGenres: [],
  artistTagline: "",
  artistProfileImage: null,
  artistInstagram: "",
  artistTiktok: "",
  artistWebsite: "",
  artistSpotify: "",
  fanDisplayName: "",
  fanCity: "",
  fanCountry: "",
  fanGenres: [],
  fanProfileImage: null,
};

const STORAGE_KEY = 'bortal_signup_userType';
const EMAIL_STORAGE_KEY = 'bortal_signup_email';

const SignupWizardContext = createContext<SignupWizardContextType | undefined>(undefined);

export const SignupWizardProvider = ({ children }: { children: ReactNode }) => {
  const [data, setData] = useState<SignupData>(() => {
    // Initialize with stored userType and email if available
    const storedUserType = localStorage.getItem(STORAGE_KEY) as UserType;
    const storedEmail = localStorage.getItem(EMAIL_STORAGE_KEY) || "";
    return {
      ...initialData,
      userType: storedUserType,
      email: storedEmail,
    };
  });
  const [currentStep, setCurrentStep] = useState(1);

  // Persist userType and email to localStorage when they change
  useEffect(() => {
    if (data.userType) {
      localStorage.setItem(STORAGE_KEY, data.userType);
    }
    if (data.email) {
      localStorage.setItem(EMAIL_STORAGE_KEY, data.email);
    }
  }, [data.userType, data.email]);

  const updateData = (updates: Partial<SignupData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  };

  const resetData = () => {
    setData(initialData);
    setCurrentStep(1);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(EMAIL_STORAGE_KEY);
  };

  return (
    <SignupWizardContext.Provider value={{ data, updateData, resetData, currentStep, setCurrentStep }}>
      {children}
    </SignupWizardContext.Provider>
  );
};

export const useSignupWizard = () => {
  const context = useContext(SignupWizardContext);
  if (context === undefined) {
    throw new Error("useSignupWizard must be used within a SignupWizardProvider");
  }
  return context;
};
